import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Loader2, PhoneCall, CalendarClock, FileDown, Linkedin } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackLeadConversion } from "../lib/gtag";
import { CONTACT, HEADER_IMG } from "../data/site";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackLeadConversion("contact");
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const wa = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi SourceX Global, I'd like to discuss a sourcing requirement.")}`;
  const callback = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Callback Request — SourceX Global")}&body=${encodeURIComponent("Please call me back. My number is: ")}`;
  const meeting = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Schedule a Meeting — SourceX Global")}`;
  const profile = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Request Company Profile — SourceX Global")}`;

  return (
    <div data-testid="contact-page">
      <PageHeader
        overline="Contact"
        title="Let's talk sourcing."
        subtitle="Reach out and our team will respond within one business day."
        bgImage={HEADER_IMG.contact}
      />
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8">
          <div className="space-y-px bg-border border border-border">
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-btn" className="bg-card flex items-center gap-4 p-6 group hover:bg-secondary transition-colors">
              <Mail className="text-brand-orange" size={22} />
              <div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground">Email</div>
                <div className="font-display font-bold group-hover:text-brand-orange transition-colors break-all">{CONTACT.email}</div>
              </div>
            </a>
            <a href={wa} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-card" className="bg-card flex items-center gap-4 p-6 group hover:bg-secondary transition-colors">
              <MessageCircle className="text-brand-orange" size={22} />
              <div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground">WhatsApp</div>
                <div className="font-display font-bold group-hover:text-brand-orange transition-colors break-all">{CONTACT.phone}</div>
              </div>
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} className="bg-card flex items-center gap-4 p-6 group hover:bg-secondary transition-colors">
              <Phone className="text-brand-orange" size={22} />
              <div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground">Phone</div>
                <div className="font-display font-bold">{CONTACT.phone}</div>
              </div>
            </a>
            <div className="bg-card flex items-center gap-4 p-6">
              <MapPin className="text-brand-orange" size={22} />
              <div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground">Address</div>
                <div className="font-display font-bold">{CONTACT.address}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a href={wa} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3.5 font-mono uppercase text-[10px] tracking-[0.15em] hover:opacity-90 transition-opacity"><MessageCircle size={15} /> WhatsApp</a>
            <a href={callback} data-testid="contact-callback-btn" className="inline-flex items-center justify-center gap-2 bg-navy text-white px-4 py-3.5 font-mono uppercase text-[10px] tracking-[0.15em] hover:bg-navy-soft transition-colors"><PhoneCall size={15} /> Callback</a>
            <a href={meeting} data-testid="contact-meeting-btn" className="inline-flex items-center justify-center gap-2 border border-border px-4 py-3.5 font-mono uppercase text-[10px] tracking-[0.15em] hover:border-brand-orange hover:text-brand-orange transition-colors"><CalendarClock size={15} /> Video Meeting</a>
            <a href={profile} data-testid="download-profile" className="inline-flex items-center justify-center gap-2 border border-border px-4 py-3.5 font-mono uppercase text-[10px] tracking-[0.15em] hover:border-brand-orange hover:text-brand-orange transition-colors"><FileDown size={15} /> Company Profile</a>
          </div>

          <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-brand-orange transition-colors text-sm">
            <Linkedin size={16} /> Follow us on LinkedIn
          </a>

          <div className="border border-border">
            <iframe title="SourceX Global Location" data-testid="contact-map"
              src="https://www.google.com/maps?q=Pune,Maharashtra,India&output=embed"
              className="w-full h-64 grayscale contrast-125" loading="lazy" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6 border border-border p-8 md:p-10 bg-card">
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={labelCls}>Name *</label><input data-testid="contact-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Full name" /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" /></div>
              <div><label className={labelCls}>Phone</label><input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 ..." /></div>
              <div><label className={labelCls}>Company</label><input data-testid="contact-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" /></div>
            </div>
            <div><label className={labelCls}>Message *</label><textarea data-testid="contact-message" required rows={6} value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help you source?" /></div>
            <button data-testid="contact-submit" type="submit" disabled={loading}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
